import * as React from "react";
import { gql, useMutation, ApolloError } from "@apollo/client";
import {
  UpdatePlatformContactMutationMutation,
  UpdatePlatformContactMutationMutationVariables
} from "../../../../../../schemaTypes";

export const updatePlatformContactMutation = gql`
  mutation UpdatePlatformContactMutation(
    $id: String!,
    $input: UpdatePlatformContactInput!
  ) {
    updatePlatformContact(
      id: $id,
      input: $input
    ) {
      errors {
        path
        message
      }
      message
      logoUrl
    }
  }
`;

// Utility to convert File to base64 data URL
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface Props {
  children: (data: {
    submit: (
      values: UpdatePlatformContactMutationMutationVariables & { logoFile?: File }
    ) => Promise<{
      errors: { path: string; message: string }[] | null;
      message?: string | null;
      logoUrl?: string | null;
    }>;
    loading: boolean;
    error?: ApolloError;
  }) => React.ReactElement | null;
}

export const UpdatePlatformContact: React.FC<Props> = ({ children }) => {
  const [updatePlatformContactMutationFn, { loading, error }] = useMutation<
    UpdatePlatformContactMutationMutation,
    UpdatePlatformContactMutationMutationVariables
  >(updatePlatformContactMutation);

  const submit = async (
    values: UpdatePlatformContactMutationMutationVariables & { logoFile?: File }
  ) => {
    try {
      const { logoFile, input, ...rest } = values;
      let logoUrl = input.logoUrl;
      // Convert file to base64 if provided
      if (logoFile) {
        logoUrl = await fileToBase64(logoFile);
      }
      // Always send the possibly updated logoUrl
      const { data } = await updatePlatformContactMutationFn({
        variables: {
          ...rest,
          input: {
            ...input,
            logoUrl,
          },
        },
      });

      const errors = data?.updatePlatformContact?.errors ?? null;
      const message = data?.updatePlatformContact?.message ?? null;
      const returnedLogoUrl = data?.updatePlatformContact?.logoUrl ?? logoUrl;

      return {
        errors,
        message,
        logoUrl: returnedLogoUrl,
      };
    } catch (e: any) {
      return {
        errors: [{ path: "general", message: e.message || "Update failed. Please try again." }],
        message: undefined,
        logoUrl: undefined,
      };
    }
  };

  return children({ submit, loading, error });
};
